// Pure aggregation helpers for the Live Activity admin page.
//
// The getActiveSessions cloud function returns a flat list of sessions, each
// carrying its own devices[]. Operators don't think in sessions — they think
// in BOATS. These helpers collapse the flat list into a per-boat view:
//
//   sail_no  →  device_id  →  session
//
// A single boat can produce many sessions in a day (app restarts, multiple
// crew devices), so we invert the session→devices relationship into
// device→sessions for display. No new data is fetched; this is pure shaping
// on the existing API response.

// A device is "transmitting" if its most recent event landed within this
// window. Matches the freshness threshold the old DeviceRow used.
export const FRESH_MS = 120_000; // 2 min

function startMs(session) {
  const t = new Date(session?.startedAt).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Age (ms) of a device's last event relative to nowMs, or null if unknown. */
export function deviceAgeMs(device, nowMs) {
  if (!device?.lastEventTime) return null;
  const t = new Date(device.lastEventTime).getTime();
  if (Number.isNaN(t)) return null;
  return nowMs - t;
}

export function isDeviceFresh(device, nowMs) {
  const age = deviceAgeMs(device, nowMs);
  return age != null && age < FRESH_MS;
}

/** Smallest last-event age (ms) across a session's devices, or null. */
export function sessionFreshnessMs(session, nowMs) {
  let best = null;
  for (const d of session?.devices || []) {
    const age = deviceAgeMs(d, nowMs);
    if (age == null) continue;
    if (best == null || age < best) best = age;
  }
  return best;
}

/**
 * Classify a single session:
 *  - 'transmitting' — status active AND a device fired recently
 *  - 'orphaned'     — status active BUT no recent events (likely dead session)
 *  - 'finished'     — status anything other than active
 */
export function classifySession(session, nowMs) {
  if (session?.status !== 'active') return 'finished';
  const age = sessionFreshnessMs(session, nowMs);
  return age != null && age < FRESH_MS ? 'transmitting' : 'orphaned';
}

function uniq(values) {
  return [...new Set(values.filter(Boolean))];
}

const BOAT_STATUS_ORDER = { transmitting: 0, mixed: 1, stale: 2, finished: 3 };

/**
 * Collapse a flat session list into per-boat groups.
 *
 * @param {Array} sessions - flat sessions (active + finished), each with devices[]
 * @param {number} nowMs   - reference time (injected for deterministic tests)
 * @returns {Array} boat objects, sorted live-first then freshest-first
 *
 * Each boat object:
 *   { sailNo, status, classes, clubs, multiClass,
 *     sessions, sessionCount, activeCount, orphanCount, finishedCount,
 *     deviceCount, deviceGroups, lastEventMs }
 *
 * status: 'transmitting' | 'mixed' | 'stale' | 'finished'
 *   transmitting — at least one live session, none orphaned
 *   mixed        — both live and orphaned sessions
 *   stale        — orphaned session(s) but nothing live
 *   finished     — no active-status sessions at all
 *
 * deviceGroups invert session→devices into device→sessions. A session with two
 * devices appears under both device groups (informative, not a bug). Each entry
 * is { deviceId, session, device } where device is that session's device row.
 */
export function groupSessionsByBoat(sessions, nowMs) {
  const byBoat = new Map();
  for (const s of sessions || []) {
    const sailNo = s.vesselId || s.vessel || '—';
    if (!byBoat.has(sailNo)) byBoat.set(sailNo, []);
    byBoat.get(sailNo).push(s);
  }

  const boats = [];
  for (const [sailNo, boatSessions] of byBoat) {
    const sorted = [...boatSessions].sort((a, b) => startMs(b) - startMs(a));

    let activeCount = 0;
    let orphanCount = 0;
    let finishedCount = 0;
    for (const s of sorted) {
      const c = classifySession(s, nowMs);
      if (c === 'transmitting') activeCount += 1;
      else if (c === 'orphaned') orphanCount += 1;
      else finishedCount += 1;
    }

    let status;
    if (activeCount > 0 && orphanCount > 0) status = 'mixed';
    else if (activeCount > 0) status = 'transmitting';
    else if (orphanCount > 0) status = 'stale';
    else status = 'finished';

    // Invert session→devices into device→sessions.
    const deviceMap = new Map();
    for (const s of sorted) {
      for (const d of s.devices || []) {
        const key = d.deviceId || '?';
        if (!deviceMap.has(key)) deviceMap.set(key, []);
        deviceMap.get(key).push({ session: s, device: d });
      }
    }

    const deviceGroups = [...deviceMap.entries()].map(([deviceId, entries]) => {
      const sortedEntries = [...entries].sort(
        (a, b) => startMs(b.session) - startMs(a.session),
      );
      let groupLastEventMs = null;
      for (const e of sortedEntries) {
        const age = deviceAgeMs(e.device, nowMs);
        if (age == null) continue;
        if (groupLastEventMs == null || age < groupLastEventMs) groupLastEventMs = age;
      }
      return {
        deviceId,
        entries: sortedEntries,
        sessionCount: sortedEntries.length,
        lastEventMs: groupLastEventMs,
      };
    });

    // Freshest device group first; unknown-age groups last.
    deviceGroups.sort((a, b) => {
      const am = a.lastEventMs == null ? Infinity : a.lastEventMs;
      const bm = b.lastEventMs == null ? Infinity : b.lastEventMs;
      if (am !== bm) return am - bm;
      return String(a.deviceId).localeCompare(String(b.deviceId));
    });

    // Sessions that reported no devices still need to surface in the expander.
    const sessionsWithoutDevices = sorted.filter(
      (s) => !(s.devices && s.devices.length),
    );

    let lastEventMs = null;
    for (const s of sorted) {
      const age = sessionFreshnessMs(s, nowMs);
      if (age == null) continue;
      if (lastEventMs == null || age < lastEventMs) lastEventMs = age;
    }

    const classes = uniq(sorted.map((s) => s.fleetClass));
    const clubs = uniq(sorted.map((s) => s.club));

    boats.push({
      sailNo,
      status,
      classes,
      clubs,
      multiClass: classes.length > 1,
      sessions: sorted,
      sessionCount: sorted.length,
      activeCount,
      orphanCount,
      finishedCount,
      deviceCount: deviceMap.size,
      deviceGroups,
      sessionsWithoutDevices,
      lastEventMs,
    });
  }

  boats.sort((a, b) => {
    const ao = BOAT_STATUS_ORDER[a.status];
    const bo = BOAT_STATUS_ORDER[b.status];
    if (ao !== bo) return ao - bo;
    const am = a.lastEventMs == null ? Infinity : a.lastEventMs;
    const bm = b.lastEventMs == null ? Infinity : b.lastEventMs;
    if (am !== bm) return am - bm;
    return String(a.sailNo).localeCompare(String(b.sailNo));
  });

  return boats;
}
